/* ============================================================
   Duftöle Carousel — color-shifting product showcase + modal
   ============================================================ */
(function () {
  'use strict';

  // ---------- Color helpers ----------
  function hexToRgb(hex) {
    if (!hex) return { r: 26, g: 26, b: 26 };
    const h = hex.replace('#', '').trim();
    const value = h.length === 3
      ? h.split('').map(c => c + c).join('')
      : h.padEnd(6, '0').slice(0, 6);
    const num = parseInt(value, 16);
    if (Number.isNaN(num)) return { r: 26, g: 26, b: 26 };
    return {
      r: (num >> 16) & 0xff,
      g: (num >> 8) & 0xff,
      b: num & 0xff,
    };
  }

  function rgbToHex({ r, g, b }) {
    const toHex = (n) => Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0');
    return '#' + toHex(r) + toHex(g) + toHex(b);
  }

  function darken(rgb, amount = 0.5) {
    return { r: rgb.r * amount, g: rgb.g * amount, b: rgb.b * amount };
  }

  function ensureMinBrightness(rgb, min = 40) {
    const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    if (brightness >= min) return rgb;
    const factor = min / Math.max(brightness, 1);
    return {
      r: Math.min(255, rgb.r * factor),
      g: Math.min(255, rgb.g * factor),
      b: Math.min(255, rgb.b * factor),
    };
  }

  function ensureMaxBrightness(rgb, max = 130) {
    // Keep bg colors dark-ish so white text stays legible
    const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    if (brightness <= max) return rgb;
    const factor = max / brightness;
    return { r: rgb.r * factor, g: rgb.g * factor, b: rgb.b * factor };
  }

  function boostSaturation(rgb, amount = 1.4) {
    // Convert to HSL, boost S, back to RGB
    const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) { h = s = 0; }
    else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
        case g: h = ((b - r) / d + 2); break;
        default: h = ((r - g) / d + 4); break;
      }
      h /= 6;
    }
    s = Math.min(1, s * amount);
    function hueToRgb(p, q, t) {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    }
    let R, G, B;
    if (s === 0) { R = G = B = l; }
    else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      R = hueToRgb(p, q, h + 1 / 3);
      G = hueToRgb(p, q, h);
      B = hueToRgb(p, q, h - 1 / 3);
    }
    return { r: R * 255, g: G * 255, b: B * 255 };
  }

  // ---------- Vibrant dominant color extraction (client-side) ----------
  function extractDominantColor(img) {
    return new Promise((resolve) => {
      const finish = () => {
        try {
          const canvas = document.createElement('canvas');
          const size = 64;
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, size, size);
          const data = ctx.getImageData(0, 0, size, size).data;
          const buckets = Object.create(null);
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
            if (a < 200) continue; // skip near-transparent
            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            const lum = (max + min) / 2;
            const sat = max === 0 ? 0 : (max - min) / max;
            // Skip near-white, near-black, near-grey to find a vibrant tone
            if (sat < 0.18) continue;
            if (lum < 28 || lum > 232) continue;
            // Quantize into 16-step buckets to group similar colors
            const key = (r >> 4) + '_' + (g >> 4) + '_' + (b >> 4);
            const bucket = buckets[key] || (buckets[key] = { r: 0, g: 0, b: 0, count: 0 });
            bucket.r += r;
            bucket.g += g;
            bucket.b += b;
            bucket.count += 1;
          }
          let best = null;
          let bestCount = 0;
          for (const k in buckets) {
            if (buckets[k].count > bestCount) {
              best = buckets[k];
              bestCount = buckets[k].count;
            }
          }
          if (!best || bestCount === 0) {
            resolve(null);
            return;
          }
          resolve({
            r: best.r / best.count,
            g: best.g / best.count,
            b: best.b / best.count,
          });
        } catch (e) {
          // Likely a tainted canvas (CORS) — caller will fall back
          resolve(null);
        }
      };

      if (img.complete && img.naturalWidth > 0) {
        finish();
      } else {
        img.addEventListener('load', finish, { once: true });
        img.addEventListener('error', () => resolve(null), { once: true });
      }
    });
  }

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          style: 'currency',
          currency: window.Shopify.currency.active,
        }).format(cents / 100);
      } catch (e) { /* fall through */ }
    }
    return (cents / 100).toFixed(2) + ' €';
  }

  // ---------- Carousel ----------
  class DuftoeleCarousel {
    constructor(root) {
      this.root = root;
      this.items = Array.from(root.querySelectorAll('.duftoele-carousel__item'));
      if (this.items.length === 0) return;

      this.viewport = root.querySelector('.duftoele-carousel__viewport');
      this.track = root.querySelector('.duftoele-carousel__track');
      this.prevBtn = root.querySelector('.duftoele-carousel__arrow--prev');
      this.nextBtn = root.querySelector('.duftoele-carousel__arrow--next');
      this.pagination = root.querySelector('.duftoele-carousel__pagination');
      this.modal = document.getElementById(root.dataset.modalId);

      this.activeIndex = 0;
      this.itemSpacing = this.computeSpacing();

      this.buildPagination();
      this.bindEvents();
      this.layout();
      // Apply initial color from server-rendered hint, then upgrade asynchronously
      this.applyColor(this.items[0].dataset.color);

      if (this.modal) {
        this.bindModal();
      }

      // Upgrade each item's color by sampling its actual image
      this.upgradeColors();

      window.addEventListener('resize', this.onResize.bind(this));
    }

    upgradeColors() {
      this.items.forEach((item, i) => {
        const img = item.querySelector('.duftoele-carousel__image');
        if (!img) return;
        extractDominantColor(img).then((rgb) => {
          if (!rgb) return;
          // Boost saturation a bit for richer backgrounds
          const boosted = boostSaturation(rgb, 1.35);
          const hex = rgbToHex(boosted);
          item.dataset.color = hex;
          // If this image's product is currently shown, re-apply
          if (i === this.activeIndex) {
            this.applyColor(hex);
          }
        });
      });
    }

    computeSpacing() {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      return isMobile ? 190 : 300;
    }

    onResize() {
      this.itemSpacing = this.computeSpacing();
      this.layout();
    }

    buildPagination() {
      if (!this.pagination) return;
      this.pagination.innerHTML = '';
      this.dots = this.items.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'duftoele-carousel__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Gehe zu Folie ' + (i + 1));
        dot.addEventListener('click', () => this.goTo(i));
        this.pagination.appendChild(dot);
        return dot;
      });
    }

    bindEvents() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.activeIndex + 1));

      this.items.forEach((item, i) => {
        item.addEventListener('click', () => {
          if (i === this.activeIndex) {
            this.openModal();
          } else {
            this.goTo(i);
          }
        });
      });

      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.goTo(this.activeIndex - 1);
        if (e.key === 'ArrowRight') this.goTo(this.activeIndex + 1);
        if (e.key === 'Enter') this.openModal();
      });

      let touchStartX = null;
      let touchStartY = null;
      this.viewport && this.viewport.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });

      this.viewport && this.viewport.addEventListener('touchend', (e) => {
        if (touchStartX === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) this.goTo(this.activeIndex + 1);
          else this.goTo(this.activeIndex - 1);
        }
        touchStartX = null;
        touchStartY = null;
      });

      let wheelLock = false;
      this.viewport && this.viewport.addEventListener('wheel', (e) => {
        if (this.modal && this.modal.classList.contains('is-open')) return;
        const dominant = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        if (Math.abs(dominant) < 6 || wheelLock) return;
        wheelLock = true;
        if (dominant > 0) this.goTo(this.activeIndex + 1);
        else this.goTo(this.activeIndex - 1);
        setTimeout(() => { wheelLock = false; }, 700);
      }, { passive: true });
    }

    bindModal() {
      const closeBtn = this.modal.querySelector('.duftoele-modal__close');
      const backdrop = this.modal.querySelector('.duftoele-modal__backdrop');
      if (closeBtn) closeBtn.addEventListener('click', () => this.closeModal());
      if (backdrop) backdrop.addEventListener('click', () => this.closeModal());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
          this.closeModal();
        }
      });

      this.modal.querySelectorAll('.duftoele-modal__panel').forEach((panel) => {
        panel.querySelectorAll('.duftoele-modal__variant-btn').forEach((btn) => {
          btn.addEventListener('click', () => {
            if (btn.classList.contains('is-sold-out')) return;
            panel.querySelectorAll('.duftoele-modal__variant-btn').forEach((b) => b.classList.remove('is-selected'));
            btn.classList.add('is-selected');
            const variantId = btn.dataset.variantId;
            const price = parseInt(btn.dataset.variantPrice, 10);
            const compareAt = parseInt(btn.dataset.variantCompare, 10);
            const idInput = panel.querySelector('input[name="id"]');
            if (idInput) idInput.value = variantId;
            const priceEl = panel.querySelector('.duftoele-modal__price-value');
            if (priceEl && !Number.isNaN(price)) priceEl.textContent = formatMoney(price);
            const compareEl = panel.querySelector('.duftoele-modal__price-compare');
            const priceWrap = panel.querySelector('.duftoele-modal__price');
            if (compareEl && priceWrap) {
              if (!Number.isNaN(compareAt) && compareAt > price) {
                compareEl.textContent = formatMoney(compareAt);
                compareEl.hidden = false;
                priceWrap.classList.add('is-on-sale');
              } else {
                compareEl.hidden = true;
                priceWrap.classList.remove('is-on-sale');
              }
            }
          });
        });

        const qtyValue = panel.querySelector('.duftoele-modal__qty-value');
        const qtyHidden = panel.querySelector('input[name="quantity"]');
        panel.querySelectorAll('[data-qty-action]').forEach((btn) => {
          btn.addEventListener('click', () => {
            let val = parseInt(qtyValue.textContent, 10) || 1;
            val = btn.dataset.qtyAction === 'inc' ? val + 1 : Math.max(1, val - 1);
            qtyValue.textContent = val;
            qtyHidden.value = val;
          });
        });

        const form = panel.querySelector('.duftoele-modal__form');
        if (form) {
          form.addEventListener('submit', async (e) => {
            e.preventDefault();
            const cta = form.querySelector('.duftoele-modal__cta');
            const original = cta.textContent;
            cta.disabled = true;
            cta.textContent = 'Wird hinzugefügt…';
            try {
              const data = new FormData(form);
              const route = (window.Shopify && window.Shopify.routes)
                ? window.Shopify.routes.root + 'cart/add.js'
                : '/cart/add.js';
              const res = await fetch(route, {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: data,
              });
              if (!res.ok) throw new Error('add failed');
              cta.textContent = 'Hinzugefügt ✓';
              cta.classList.add('is-added');
              document.dispatchEvent(new CustomEvent('cart:refresh'));
              setTimeout(() => {
                cta.textContent = original;
                cta.classList.remove('is-added');
                cta.disabled = false;
              }, 1800);
            } catch (err) {
              console.error(err);
              cta.textContent = 'Erneut versuchen';
              setTimeout(() => {
                cta.textContent = original;
                cta.disabled = false;
              }, 1500);
            }
          });
        }
      });
    }

    goTo(index) {
      const total = this.items.length;
      let target = index;
      if (target < 0) target = total - 1;
      if (target >= total) target = 0;
      if (target === this.activeIndex) return;
      this.activeIndex = target;
      this.layout();
      this.applyColor(this.items[this.activeIndex].dataset.color);
      this.updatePagination();
    }

    layout() {
      this.items.forEach((item, i) => {
        const offset = i - this.activeIndex;
        const distance = Math.abs(offset);
        const x = offset * this.itemSpacing;
        const scale = distance === 0 ? 1 : Math.max(0.55, 1 - distance * 0.18);
        const opacity = distance === 0 ? 1 : Math.max(0.15, 0.6 - (distance - 1) * 0.18);
        item.style.transform =
          'translate(-50%, -50%) translateX(' + x + 'px) scale(' + scale + ')';
        item.style.opacity = opacity;
        item.style.zIndex = 10 - distance;
        item.classList.toggle('is-active', i === this.activeIndex);
        item.setAttribute('aria-hidden', i === this.activeIndex ? 'false' : 'true');
      });
    }

    applyColor(hex) {
      // Ensure dark-enough background so text stays legible
      const rgb = ensureMaxBrightness(ensureMinBrightness(hexToRgb(hex), 30), 120);
      const safe = rgbToHex(rgb);
      // Set the background color directly so the CSS transition fires
      this.root.style.backgroundColor = safe;
      if (this.modal) {
        const dialog = this.modal.querySelector('.duftoele-modal__dialog');
        if (dialog) dialog.style.backgroundColor = safe;
      }
    }

    updatePagination() {
      if (!this.dots) return;
      this.dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === this.activeIndex);
      });
    }

    openModal() {
      if (!this.modal) return;
      const idx = this.activeIndex;
      this.modal.querySelectorAll('.duftoele-modal__panel').forEach((p) => {
        p.classList.toggle('is-current', parseInt(p.dataset.index, 10) === idx);
      });
      this.applyColor(this.items[idx].dataset.color);
      this.modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      const closeBtn = this.modal.querySelector('.duftoele-modal__close');
      if (closeBtn) setTimeout(() => closeBtn.focus(), 50);
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }

  function init() {
    document.querySelectorAll('[data-duftoele-carousel]').forEach((el) => {
      new DuftoeleCarousel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
